import React, { useRef, useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { PublicLayout, Section, Eyebrow } from "@/components/PublicLayout";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { CreditCard, Smartphone, Loader2, CheckCircle2, ArrowRight, Minus, Plus, Timer } from "lucide-react";

const DONE = ["completed", "complete", "success", "successful", "paid"];
const FAIL = ["failed", "cancelled", "canceled", "rejected", "expired"];

function PromoCountdown({ hours, label }) {
  const total = Math.max(1, (hours || 24)) * 3600;
  const [rem, setRem] = useState(total - (Math.floor(Date.now() / 1000) % total));
  useEffect(() => {
    const id = setInterval(() => setRem(total - (Math.floor(Date.now() / 1000) % total)), 1000);
    return () => clearInterval(id);
  }, [total]);
  const h = String(Math.floor(rem / 3600)).padStart(2, "0");
  const m = String(Math.floor((rem % 3600) / 60)).padStart(2, "0");
  const s = String(rem % 60).padStart(2, "0");
  return (
    <div data-testid="sale-promo-banner" className="mt-5 flex items-center gap-3 rounded-sm border border-heraldic/40 bg-heraldic/10 px-4 py-3 max-w-md">
      <Timer className="h-5 w-5 text-heraldic shrink-0" />
      <div className="text-sm text-ink/80">{label || "Offre spéciale — se termine dans"} <span data-testid="sale-promo-timer" className="font-mono font-semibold text-heraldic tabular-nums">{h}:{m}:{s}</span></div>
    </div>
  );
}

export default function SaleLinkPay() {
  const { slug } = useParams();
  const [link, setLink] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [qty, setQty] = useState(1);
  const [method, setMethod] = useState(null);
  const [loading, setLoading] = useState(false);
  const [payment, setPayment] = useState(null);
  const [status, setStatus] = useState(null);
  const [done, setDone] = useState(false);
  const [msisdn, setMsisdn] = useState("");
  const [country, setCountry] = useState("CM");
  const [correspondent, setCorrespondent] = useState("MTN_MOMO_CMR");
  const [countries, setCountries] = useState([]);
  const mounted = useRef(false);

  useEffect(() => {
    api.get(`/sale-links/${slug}`).then((r) => setLink(r.data)).catch(() => setNotFound(true));
    api.get("/pay/pawapay/countries").then((r) => setCountries(r.data.countries || [])).catch(() => {});
  }, [slug]);

  const ops = (countries.find((c) => c.code === country)?.operators) || [];
  const onCountry = (c) => { setCountry(c); const co = countries.find((x) => x.code === c); setCorrespondent(co?.operators?.[0]?.code || ""); };
  const maxQty = link?.quantity_enabled ? Math.max(1, link?.max_quantity || 5) : 1;
  const effQty = link?.quantity_enabled ? Math.min(qty, maxQty) : 1;
  const total = link ? +(link.amount * effQty).toFixed(2) : 0;

  const pollStatus = (pid) => {
    let n = 0;
    const tick = async () => {
      n += 1;
      try {
        const { data } = await api.get(`/pay/nucleus/status/${pid}`);
        setStatus(data.status);
        const s = (data.status || "").toLowerCase();
        if (DONE.includes(s)) { toast.success("Paiement confirmé."); setDone(true); return; }
        if (FAIL.includes(s)) { toast.error("Paiement échoué ou annulé. Veuillez réessayer."); return; }
      } catch {}
      if (n < 100) setTimeout(tick, 3000);
    };
    tick();
  };

  const startCard = async () => {
    if (!name.trim()) return toast.error("Entrez votre nom.");
    setMethod("stripe"); setLoading(true);
    try {
      const { data } = await api.post(`/sale-links/${slug}/pay`, { name, email: email || undefined, method: "stripe", country, quantity: effQty });
      setPayment(data);
      if (!mounted.current && window.Stripe && data.stripe) {
        mounted.current = true;
        const stripe = window.Stripe(data.stripe.publishable_key);
        const checkout = await stripe.initEmbeddedCheckout({ clientSecret: data.stripe.client_secret });
        checkout.mount("#checkout");
      }
      pollStatus(data.payment_id);
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); setMethod(null); }
    finally { setLoading(false); }
  };

  const startMomo = async () => {
    if (!name.trim()) return toast.error("Entrez votre nom.");
    if (!msisdn) return toast.error("Entrez votre numéro Mobile Money.");
    setLoading(true);
    try {
      const { data } = await api.post(`/sale-links/${slug}/pay`, { name, email: email || undefined, method: "pawapay", msisdn, country, correspondent, quantity: effQty });
      setPayment(data); pollStatus(data.payment_id);
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setLoading(false); }
  };

  if (notFound) return (
    <PublicLayout><Section className="py-16 max-w-2xl text-center"><h1 className="font-serif text-3xl text-ink">Lien introuvable</h1><p className="mt-3 text-ink/60">Ce lien de paiement est introuvable ou n'est plus actif.</p></Section></PublicLayout>
  );
  if (!link) return <PublicLayout><Section className="py-16 max-w-2xl"><Loader2 className="h-5 w-5 animate-spin text-heraldic" /></Section></PublicLayout>;

  if (done) return (
    <PublicLayout><Section className="py-16 max-w-2xl text-center" data-testid="sale-pay-done">
      <CheckCircle2 className="h-12 w-12 text-heraldic mx-auto" />
      <h1 className="mt-4 font-serif text-3xl text-ink">Paiement confirmé</h1>
      <p className="mt-3 text-ink/60">Merci, {name}. Votre paiement pour « {link.title_fr} » a bien été reçu. Un reçu vous est envoyé automatiquement.</p>
    </Section></PublicLayout>
  );

  return (
    <PublicLayout>
      <Section className="py-12 md:py-16 max-w-2xl" data-testid="sale-pay-page">
        <Eyebrow>Paiement · Simo University</Eyebrow>
        <h1 className="font-serif text-4xl md:text-5xl tracking-tight text-ink" data-testid="sale-pay-title">{link.title_fr}</h1>
        {link.reason_fr && <p className="mt-3 text-ink/60">{link.reason_fr}</p>}
        <p className="mt-2 text-ink/70" data-testid="sale-pay-amount">Montant : <strong data-testid="sale-pay-total">{total} {link.currency}</strong>{link.quantity_enabled && effQty > 1 ? <span className="text-ink/50"> ({link.amount} {link.currency} × {effQty})</span> : null}. Nucleus Pay convertit automatiquement le montant selon votre pays.</p>

        {link.promo_enabled && <PromoCountdown hours={link.promo_hours} label={link.promo_label_fr} />}

        {!method && (
          <div className="mt-8 space-y-4 max-w-md">
            {link.quantity_enabled && (
              <div>
                <label className="block text-sm text-ink/60 mb-1">{link.quantity_label_fr || "Nombre d'enfants"}</label>
                <div className="inline-flex items-center border border-border rounded-sm overflow-hidden" data-testid="sale-qty">
                  <button type="button" data-testid="sale-qty-minus" onClick={() => setQty((q) => Math.max(1, q - 1))} className="px-3 py-2 text-ink/70 hover:bg-ink/5"><Minus className="h-4 w-4" /></button>
                  <span className="px-5 py-2 text-ink font-medium tabular-nums" data-testid="sale-qty-value">{effQty}</span>
                  <button type="button" data-testid="sale-qty-plus" onClick={() => setQty((q) => Math.min(maxQty, q + 1))} className="px-3 py-2 text-ink/70 hover:bg-ink/5"><Plus className="h-4 w-4" /></button>
                </div>
                <p className="mt-1 text-xs text-ink/45">Jusqu'à {maxQty}. Le total se met à jour automatiquement.</p>
              </div>
            )}
            <div>
              <label className="block text-sm text-ink/60 mb-1">Votre nom</label>
              <Input data-testid="sale-payer-name" value={name} onChange={(e) => setName(e.target.value)} placeholder="Nom et prénom" className="rounded-sm" />
            </div>
            <div>
              <label className="block text-sm text-ink/60 mb-1">Email (optionnel — pour recevoir le reçu)</label>
              <Input data-testid="sale-payer-email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="vous@exemple.com" className="rounded-sm" />
            </div>
            <div className="grid sm:grid-cols-2 gap-3 pt-2">
              <button data-testid="sale-method-card" onClick={startCard} className="border border-border rounded-sm p-6 text-left hover:border-heraldic transition-colors">
                <CreditCard className="h-6 w-6 text-heraldic" /><div className="mt-3 font-serif text-xl text-ink">Carte bancaire</div><div className="text-sm text-ink/55">Visa, Mastercard — paiement sécurisé.</div>
              </button>
              <button data-testid="sale-method-momo" onClick={() => setMethod("pawapay")} className="border border-border rounded-sm p-6 text-left hover:border-heraldic transition-colors">
                <Smartphone className="h-6 w-6 text-heraldic" /><div className="mt-3 font-serif text-xl text-ink">Mobile Money</div><div className="text-sm text-ink/55">MTN, Orange — confirmation par code PIN.</div>
              </button>
            </div>
          </div>
        )}

        {method === "stripe" && (
          <div className="mt-8">
            {loading && <div className="flex items-center gap-2 text-ink/50"><Loader2 className="h-4 w-4 animate-spin" /> Préparation du paiement sécurisé…</div>}
            <div id="checkout" className="mt-2" />
            {status && <p className="mt-4 text-sm text-ink/50">Statut : {status}</p>}
          </div>
        )}

        {method === "pawapay" && !payment && (
          <div className="mt-8 space-y-4 max-w-sm">
            <div>
              <label className="block text-sm text-ink/60 mb-1">Pays</label>
              <select data-testid="sale-momo-country" value={country} onChange={(e) => onCountry(e.target.value)} className="w-full border border-border rounded-sm px-3 py-2 bg-white text-ink focus:border-heraldic outline-none">
                {countries.map((c) => <option key={c.code} value={c.code}>{c.name}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-sm text-ink/60 mb-1">Opérateur Mobile Money</label>
              <select data-testid="sale-momo-correspondent" value={correspondent} onChange={(e) => setCorrespondent(e.target.value)} className="w-full border border-border rounded-sm px-3 py-2 bg-white text-ink focus:border-heraldic outline-none">
                {ops.map((o) => <option key={o.code} value={o.code}>{o.label}</option>)}
              </select>
            </div>
            <div>
              <Input data-testid="sale-momo-msisdn" value={msisdn} onChange={(e) => setMsisdn(e.target.value.replace(/[^0-9]/g, ""))} inputMode="numeric" placeholder="Ex. 237670000123" className="rounded-sm" />
              <p className="mt-1 text-xs text-ink/45">Numéro avec l'indicatif pays, sans le « + ».</p>
            </div>
            <button data-testid="sale-momo-pay" onClick={startMomo} disabled={loading || !correspondent} className="inline-flex items-center gap-2 bg-heraldic text-white px-7 py-3 rounded-sm hover:bg-heraldic-700 transition-colors disabled:opacity-50">Payer par Mobile Money <ArrowRight className="h-4 w-4" /></button>
            <button onClick={() => setMethod(null)} className="block text-sm text-ink/50 underline">Retour</button>
          </div>
        )}

        {method === "pawapay" && payment && (
          <div className="mt-8 border border-border rounded-sm p-6">
            <Smartphone className="h-8 w-8 text-heraldic" />
            <p className="mt-3 text-ink/80">{payment.pawapay?.message || "Confirmez le paiement sur votre téléphone (code PIN Mobile Money)."}</p>
            <p className="mt-2 text-sm text-ink/60">Si vous ne recevez pas la demande de code PIN, ouvrez votre application Mobile Money et validez le paiement en attente. Cette page se met à jour automatiquement — ne la fermez pas.</p>
            <div className="mt-4 flex items-center gap-2 text-sm text-ink/50">
              {DONE.includes((status || "").toLowerCase()) ? <><CheckCircle2 className="h-4 w-4 text-heraldic" /> Paiement confirmé</> : <><Loader2 className="h-4 w-4 animate-spin" /> En attente de confirmation…</>}
            </div>
          </div>
        )}
      </Section>
    </PublicLayout>
  );
}
